import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Carousel,
  CarouselContent,
  CarouselDots,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "ui/Carousel";
import EntityAvatar from "components/Entities/EntityAvatar";
import AlignmentSummaryDisplay from "components/Alignment/AlignmentSummaryDisplay";
import DetailPanel from "ui/DetailPanel";
import DialogConfirmation from "ui/DialogConfirmation";
import { useUser } from "util/UserContext";
import type { ViewerRelation } from "util/constants";
import type { QuizResults as QuizResultsData } from "types/quiz";
import type { QuizUiStrings } from "i18n/quizUi";
import { formatQuizTemplate, getQuizButtonClassName, mapQuizUserToEntityAvatarUser } from "./quizUtils";
import QuizResultsPlayer from "./QuizResultsPlayer";

interface QuizResultsProps {
  ui: QuizUiStrings;
  results: QuizResultsData;
  onEndQuiz: () => Promise<void>;
  isEnding: boolean;
  onCatchUpParticipant?: (participantUserId: number) => void;
}

/**
 * Final results surface after the quiz ends.
 */
const QuizResults: React.FC<QuizResultsProps> = ({ ui, results, onEndQuiz, isEnding, onCatchUpParticipant }) => {
  const navigate = useNavigate();
  const { currentUser } = useUser();
  const [quizOnly, setQuizOnly] = useState(true);
  const [showEndDialog, setShowEndDialog] = useState(false);
  const otherParticipants = useMemo(
    () => results.participants.filter((participant) => participant.user_id !== results.viewer?.user_id).map((participant) => ({ id: participant.user_id })),
    [results.participants, results.viewer?.user_id]
  );
  const statementFilterIds = useMemo(
    () => results.teabates.map((teabate) => teabate.post_id).filter((value): value is number => typeof value === "number"),
    [results.teabates]
  );
  const hasAlignmentData = currentUser !== null && otherParticipants.length > 0 && (!quizOnly || statementFilterIds.length > 0);
  const canEndQuiz = results.quiz.can_end;
  const participantByUserId = useMemo(
    () => new Map(results.participants.map((participant) => [participant.user_id, participant])),
    [results.participants]
  );

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-6 px-4 py-8">
      <div className="space-y-2">
        <h1 className="text-3xl font-semibold text-eoe-text-heading dark:text-eoe-dark-text-heading">
          {ui.resultsTitle}
        </h1>
        <p className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">{ui.resultsDescription}</p>
      </div>

      <DetailPanel
        title={ui.scoreboardTitle}
        showTopCollapse={false}
        showBottomCollapse={true}
        className="bg-eoe-bg-modal dark:bg-eoe-dark-bg-modal"
      >
        <div className="mb-4 flex items-center justify-between gap-3">
          {results.quiz.is_creator && results.quiz.status === "part2" ? (
            <div className="flex flex-col items-end gap-2">
              {!canEndQuiz && results.quiz.status === "part2" ? (
                <p className="text-right text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub">
                  {ui.endQuizNeedsOtherComplete}
                </p>
              ) : null}
              <button
                type="button"
                onClick={() => setShowEndDialog(true)}
                disabled={!canEndQuiz || isEnding}
                className={getQuizButtonClassName({
                  disabled: Boolean(!canEndQuiz || isEnding),
                })}
              >
                {ui.endQuizAction}
              </button>
            </div>
          ) : null}
        </div>

        <div className="space-y-3">
          {results.leaderboard.map((entry) => (
            <div
              key={entry.user_id}
              className="flex items-center justify-between gap-3 rounded-2xl border border-eoe-border-main bg-eoe-bg-hover px-4 py-3 dark:border-eoe-dark-border-main dark:bg-eoe-dark-bg-hover"
            >
              <EntityAvatar
                entity={mapQuizUserToEntityAvatarUser(entry.user)}
                entityType="user"
                entityId={entry.user_id}
                entityChip={true}
                showLink={true}
                isInputField={false}
                className="min-w-0 flex-1"
              />
              <div className="flex items-center gap-3">
                <span className="text-sm font-semibold text-eoe-text dark:text-eoe-dark-text">
                  {formatQuizTemplate(ui.scoreLabel, {
                    correct: entry.correct_guess_count,
                    total: entry.total_guess_count,
                  })}
                </span>
                {results.quiz.status === "part2" && participantByUserId.get(entry.user_id)?.viewer_can_catch_up ? (
                  <button
                    type="button"
                    onClick={() => onCatchUpParticipant?.(entry.user_id)}
                    className="btn-secondary px-3 py-1.5 text-xs"
                  >
                    {ui.catchUpAction}
                  </button>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      </DetailPanel>

      <DetailPanel
        title={ui.overallAlignmentTitle}
        showTopCollapse={false}
        showBottomCollapse={true}
        className="bg-eoe-bg-modal dark:bg-eoe-dark-bg-modal"
      >
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <label className="flex items-center gap-3 text-sm text-eoe-text dark:text-eoe-dark-text">
            <span>{quizOnly ? ui.alignmentQuizOnly : ui.alignmentOverall}</span>
            <input
              type="checkbox"
              className="toggle-switch-input"
              checked={quizOnly}
              onChange={(event) => setQuizOnly(event.target.checked)}
            />
          </label>
        </div>
        {hasAlignmentData ? (
          <AlignmentSummaryDisplay
            targetUsers={otherParticipants}
            currentUser={currentUser}
            targetLabel={ui.otherPlayersLabel}
            currentUserViewerRelation={"public" as ViewerRelation}
            onStatementSelect={(statementId) => {
              navigate(`/teabate/${statementId}`);
            }}
            isTeam={true}
            statementFilterIds={quizOnly ? statementFilterIds : null}
          />
        ) : (
          <p className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">{ui.noAlignmentData}</p>
        )}
      </DetailPanel>

      <section className="space-y-4">
        <h2 className="text-base font-semibold text-eoe-text dark:text-eoe-dark-text">{ui.perPlayerResultsTitle}</h2>
        <Carousel className="w-full">
          <CarouselContent>
            {results.participants.map((participant) => (
              <CarouselItem key={participant.user_id}>
                <QuizResultsPlayer
                  ui={ui}
                  participant={participant}
                  onTeabateSelect={(statementId) => navigate(`/teabate/${statementId}`)}
                  quizOnly={quizOnly}
                  statementFilterIds={statementFilterIds}
                  allQuizTeabates={results.teabates}
                  viewerStances={results.viewer?.stances || []}
                />
              </CarouselItem>
            ))}
          </CarouselContent>
          <div className="mt-4 flex items-center justify-between">
            <CarouselPrevious hideWhenDisabled="invisible" />
            <CarouselDots />
            <CarouselNext hideWhenDisabled="invisible" />
          </div>
        </Carousel>
      </section>
      <DialogConfirmation
        isOpen={showEndDialog}
        title={ui.endQuizConfirmTitle}
        description={ui.endQuizConfirmDescription}
        confirmText={ui.endQuizConfirmAction}
        cancelText={ui.endQuizCancelAction}
        onCancel={() => setShowEndDialog(false)}
        onConfirm={() => {
          setShowEndDialog(false)
          void onEndQuiz()
        }}
      />
    </div>
  );
};

export default QuizResults;
